import styled from 'styled-components'
import { institutionalColors } from '~/design'
import FirstPageIcon from '~/meta/firstPageArrow.svg'
import LastPageIcon from '~/meta/lastPageArrow.svg'
import { formatAsBrNumber } from '~/prix'

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 46px;
  align-items: center;
  row-gap: 5px;
  padding: 15px 0;
  border-top: 1px solid #e6e6e6;

  @media (max-width: 768px) {
    padding: 12px 0;
  }
`

const PaginationButtons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;

  > button {
    border: none;
    background: none;
    font-size: 13px;
    border-radius: 50%;
    color: ${institutionalColors.neutral60};
    font-weight: 400;
    padding: 2px 6px;
    cursor: pointer;
    min-width: auto;
    transition: all 0.2s ease;

    svg {
      margin-bottom: -3.9px;
      fill: #8b99a7;
    }

    &:hover:not(:disabled):not(.active) {
      background: rgba(0, 94, 184, 0.05);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;

      svg {
        margin-bottom: -3.9px;
      }
    }
  }
`

const PageButton = styled.button<{ $active?: boolean }>`
  ${({ $active }) =>
    $active &&
    `
    border-radius: 50%;
    border: 1px solid #8fb8ef !important;
    background: #5c8fd6 !important;
    color: #fff !important;
    text-align: center;
    font-weight: 400;
  `}
`

const PageEllipsis = styled.span`
  padding: 2px 6px;
  color: ${institutionalColors.neutral60};
  font-size: 13px;
`

const PaginationInfo = styled.span`
  color: ${institutionalColors.neutral60};
  font-size: 13px;
  font-weight: 400;
`

interface PaginationFooterProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
}

export default function PaginationFooter({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: PaginationFooterProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <PaginationContainer>
      <PaginationButtons>
        <PageButton onClick={() => onPageChange(1)} disabled={currentPage === 1}>
          <FirstPageIcon width={18} height={18} />
        </PageButton>
        <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
          {'<'}
        </PageButton>

        {currentPage > 2 && <PageButton onClick={() => onPageChange(1)}>1</PageButton>}
        {currentPage > 3 && <PageEllipsis>...</PageEllipsis>}

        {currentPage > 1 && (
          <PageButton onClick={() => onPageChange(currentPage - 1)}>{currentPage - 1}</PageButton>
        )}

        <PageButton $active disabled>
          {currentPage}
        </PageButton>

        {currentPage < totalPages && (
          <PageButton onClick={() => onPageChange(currentPage + 1)}>{currentPage + 1}</PageButton>
        )}

        {currentPage + 1 < totalPages && (
          <PageButton onClick={() => onPageChange(currentPage + 2)}>{currentPage + 2}</PageButton>
        )}

        {currentPage < totalPages - 3 && <PageEllipsis>...</PageEllipsis>}

        <PageButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {'>'}
        </PageButton>
      </PaginationButtons>

      <PaginationInfo>
        Mostrando {formatAsBrNumber(startItem)} - {formatAsBrNumber(endItem)} de{' '}
        {formatAsBrNumber(totalItems)} itens
      </PaginationInfo>
    </PaginationContainer>
  )
}
